import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Button, Theme, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import GestureIcon from "@mui/icons-material/Gesture";
import styled from "@emotion/styled";
import {
  RangeSelectorControlProps,
  useChartColors,
  usePerformanceTrendColor,
} from "@hooks";
import { RangeSelector } from "@components/common";
import { useThrottledCallback } from "use-debounce";
import { DrawLineChart } from "../highcharts";
import { Figure, ChartLayout, ComparisonValue } from "../BaseChartTools";
import { SecuritiesChartMobileTooltips } from "./SecuritiesChartMobileTooltips";
import { useSecuritiesTooltips } from "./useSecuritiesTooltips";
import { ChartTypeTabs } from "./ChartTypeTabs";
import {
  SecuritiesChartDataWithDate,
  SecuritiesChartSelectedType,
  SecuritiesChartType,
} from "./types";
import { prepareSecuritiesData } from "./utils/prepareChartData";
import { SecuritiesChartFilterBar } from "./SecuritiesChartFilterBar";

const ChartActionsContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

const DrawLinesButton = styled(Button)`
  padding: 3px 5px;
  width: max-content;
  height: max-content;
  border-radius: 8px;
`;

export interface SecuritiesChartProps {
  title: string | ReactNode;
  subtitle: ReactNode;
  data: SecuritiesChartDataWithDate[];
  drawLines?: boolean;
  className?: string;
  assetTypes?: SecuritiesChartSelectedType[];
  onSelect?: (type: IntervalType, value?: RangeType) => void;
  isFetching?: boolean;
  chartType?: SecuritiesChartType;
  currency?: string;
  onChartTypeChange?: (value: SecuritiesChartType) => void;
  comparedTicker?: ComparisonValue;
  onComparedTickerChange?: (ticker?: ComparisonValue) => void;
  showNavigator?: boolean;
  showChartTypeTabs?: boolean;
  rangeProps: RangeSelectorControlProps;
  showRangeSelector?: boolean;
  showComparisonSelector?: boolean;
  showInCard?: boolean;
}

export const SecuritiesChart = ({
  data,
  title,
  subtitle,
  drawLines = false,
  className,
  assetTypes = ["derivative", "etfs", "funds", "holdings"],
  onSelect,
  isFetching,
  chartType = "performance",
  currency = "EUR",
  comparedTicker,
  onComparedTickerChange,
  onChartTypeChange,
  showNavigator,
  showChartTypeTabs = false,
  rangeProps,
  showRangeSelector = false,
  showComparisonSelector = true,
  showInCard = false,
}: SecuritiesChartProps) => {
  const { range, intervalType, selectInterval, customIntervalSelect } =
    rangeProps;
  const { t } = useTranslation();
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const colors = useChartColors("securitiesChart");
  const startValue = data[0]?.totalValue ?? 0;
  const endValue = data[data.length - 1]?.totalValue ?? 0;
  const trendColor = usePerformanceTrendColor(startValue, endValue);
  const [lineDrawingEnabled, setLineDrawingEnabled] = useState(false);
  const [isChartHovering, setIsChartHovering] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<
    SecuritiesChartSelectedType[]
  >(["all"]);
  const [selectedX, setSelectedX] = useState<number | undefined>();

  const throttledSetSelectedX = useThrottledCallback(
    (x: number | undefined) => {
      setSelectedX(x);
    },
    300
  );

  const chartColors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(colors).filter(([, value]) => typeof value === "string")
      ) as Record<string, string>,
    [colors]
  );

  const typeColors = useMemo(
    () => ({ ...chartColors, all: trendColor }),
    [chartColors, trendColor]
  );

  const isComparison = Boolean(comparedTicker);

  const formattedData = useMemo(
    () =>
      prepareSecuritiesData(
        data,
        [...selectedTypes, ...(isComparison ? ["comparison"] : [])],
        chartColors
      ),
    [data, selectedTypes, isComparison, chartColors]
  );

  const tooltips = useSecuritiesTooltips({
    x: selectedX,
    formattedData,
    currentTicker: comparedTicker,
  });

  const chartControl = (["all"] as SecuritiesChartSelectedType[])
    .concat(assetTypes)
    .map((i) => ({
      text: t(`general.chart.securitiesChart.assetTypes.${i}`),
      value: i,
      clickable: selectedTypes.includes(i) ? selectedTypes.length >= 2 : true,
    }));

  const onChartControlSelect = (value: SecuritiesChartSelectedType) => {
    const index = selectedTypes.indexOf(value);
    if (index < 0) return setSelectedTypes([...selectedTypes, value]);
    return setSelectedTypes([...selectedTypes.filter((i) => i !== value)]);
  };

  const handleClickChart = (x?: number) => {
    if (typeof x !== "number") {
      throttledSetSelectedX(undefined);
      return;
    }

    throttledSetSelectedX(x);
  };

  const handleDrawLinesClick = () => {
    setLineDrawingEnabled(!lineDrawingEnabled);
  };

  useEffect(() => {
    setLineDrawingEnabled(false);
  }, [chartType]);

  const intervalSelector = showRangeSelector ? (
    <RangeSelector
      compact={isXs}
      range={intervalType === "custom" ? range : undefined}
      intervalType={intervalType}
      onSelect={(type) => {
        selectInterval(type);
        onSelect?.(type);
      }}
      onCustomSelect={(start, end) => {
        customIntervalSelect(start, end);
        onSelect?.("custom", { startDate: start, endDate: end });
      }}
    />
  ) : undefined;

  const bottomContent: ReactNode =
    showComparisonSelector || assetTypes.length > 1 ? (
      <SecuritiesChartFilterBar
        assetTypes={assetTypes}
        chartControl={chartControl}
        selectedTypes={selectedTypes}
        onToggleType={onChartControlSelect}
        chartColors={typeColors}
        isXs={isXs}
        showComparisonSelector={showComparisonSelector}
        comparedTicker={comparedTicker}
        onComparedTickerChange={(value) => onComparedTickerChange?.(value)}
        onChartTypeChange={onChartTypeChange}
      />
    ) : undefined;

  return (
    <ChartLayout
      useCard={showInCard}
      isFetching={isFetching}
      className={className}
      intervalSelector={intervalSelector}
      figure={
        <Figure>
          <Typography variant={isXs ? "h4" : "h2"}>{title}</Typography>
          {subtitle}
        </Figure>
      }
      tooltip={
        isMd && !!tooltips ? (
          <SecuritiesChartMobileTooltips data={tooltips} />
        ) : undefined
      }
      onChartMouseEnter={() => {
        setIsChartHovering(true);
      }}
      onChartMouseLeave={() => {
        setIsChartHovering(false);
      }}
      chartTypeTabs={
        showChartTypeTabs ? (
          <ChartTypeTabs
            chartType={chartType}
            onChartTypeChange={onChartTypeChange}
          />
        ) : undefined
      }
      bottomComponent={bottomContent}
    >
      <DrawLineChart
        chartData={formattedData}
        allowDrawing={lineDrawingEnabled}
        chartType={chartType}
        currency={currency}
        comparedTicker={comparedTicker}
        onChartClick={handleClickChart}
        showTooltipMobile={false}
        showNavigator={showNavigator}
      />
      {(isChartHovering || lineDrawingEnabled) && drawLines && (
        <ChartActionsContainer>
          <DrawLinesButton
            variant="tertiary"
            startIcon={<GestureIcon />}
            size="small"
            onClick={handleDrawLinesClick}
          >
            {lineDrawingEnabled ? "Clear" : "Draw"}
          </DrawLinesButton>
        </ChartActionsContainer>
      )}
    </ChartLayout>
  );
};
